import axios, { AxiosResponse } from 'axios'
import { CaseSummaryReportGroupingTypeEnum, FilterGroupRequest, FilterRequest, ScientificAnalysisComparatorType, ScientificAnalysisEntityResultType, ScientificAnalysisFilterEntityType, ScientificAnalysisMetadataEntityFilterConditionsResponse, ScientificAnalysisSaveResponse, ScientificAnalysisTemplateHeader, SimpleListEntity, SimpleListEntityResponse } from '@/modules/scientific-analysis-module/services/scientific-analysis-dtos'
import { Nullable } from '@/shared/types/app.types'
import { ScientificAnalysis } from '@/modules/scientific-analysis-module/shared/scientific-analysis.models'
import { CommonFilterModel, CommonParams, CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { InfiniteScrollResponse } from '@/shared/models/ag-grid.model'
import { GridColumnRequest } from '@/api/services/common.dto'
import { store } from '@/store/store'

export interface ScientificAnalysisService {
    getMetadata(entityType: ScientificAnalysisFilterEntityType): Promise<ScientificAnalysisMetadataEntityFilterConditionsResponse>
    search(scientificAnalysis: ScientificAnalysis, params: CommonParams, scrollId: string, totalElements: number): Promise<InfiniteScrollResponse<ScientificAnalysisEntityResultType>>
    getFilterIcon(filterEntityType: ScientificAnalysisFilterEntityType): string
    getEntitySimpleList(entityName: SimpleListEntity, filterText: Nullable<string>): Promise<SimpleListEntityResponse[]>

    // saved templates api
    getAllTemplates(): Promise<ScientificAnalysisTemplateHeader[]>
    getTemplate(templateId: number): Promise<ScientificAnalysisSaveResponse>;
    removeTemplate(templateId: number): Promise<boolean>
    updateTemplate(templateId: number, templateName: Nullable<string>, version: Nullable<number>, template: Nullable<string>): Promise<ScientificAnalysisTemplateHeader>
    renameTemplate(templateId: number, templateName: string): Promise<ScientificAnalysisTemplateHeader>
    createTemplate(templateName: string, version: number, template: string): Promise<ScientificAnalysisTemplateHeader>
    importTemplate(file: File): Promise<ScientificAnalysisTemplateHeader[]>
    exportTemplates(templateId: number[]): Promise<boolean>
    exportTo(exportType: CommonExportType, scientificAnalysis: ScientificAnalysis, sectionLabel: string, filterModel: CommonFilterModel, sortModel: string[], columns: GridColumnRequest[], entityIdentifiers: number[]): Promise<AxiosResponse<Blob>>
}

class ScientificAnalysisServiceImpl implements ScientificAnalysisService {
    static DEFAULT_PAGE_SIZE = 1000

    public getFilterIcon(filterEntityType: ScientificAnalysisFilterEntityType): string {
        switch (filterEntityType) {
        case ScientificAnalysisFilterEntityType.CASE:
            return 'fal fa-chart-network'
        case ScientificAnalysisFilterEntityType.PATIENT:
            return 'fal fa-user-circle'
        case ScientificAnalysisFilterEntityType.SAMPLE:
            return 'fa-prescription-bottle'
        case ScientificAnalysisFilterEntityType.PORTION:
            return 'icon-block'
        case ScientificAnalysisFilterEntityType.SUBPORTION:
            return 'icon-porta'
        case ScientificAnalysisFilterEntityType.REQUEST:
            return 'fal fa-file-medical'
        case ScientificAnalysisFilterEntityType.COMPLEMENTARY:
            return 'fal fa-layer-plus'
        default:
            return ''
        }
    }

    public async getMetadata(entityType: ScientificAnalysisFilterEntityType): Promise<ScientificAnalysisMetadataEntityFilterConditionsResponse> {
        const response = await axios.get<ScientificAnalysisMetadataEntityFilterConditionsResponse>(`/scientificAnalysis/metadata?indexFilterConditions=${entityType}`)
        return response.data
    }

    public async getEntitySimpleList(entityName: SimpleListEntity, filterText: Nullable<string>): Promise<SimpleListEntityResponse[]> {
        const response = await axios.get(`/scientificAnalysis/simplelist/${entityName}?size=${ScientificAnalysisServiceImpl.DEFAULT_PAGE_SIZE}` + (filterText ? `&filter=${filterText}` : ``))
        return response.data.content
    }

    public async search(scientificAnalysis: ScientificAnalysis, params: CommonParams, scrollId: string, totalElements: number): Promise<InfiniteScrollResponse<ScientificAnalysisEntityResultType>> {
        const bodyParams = this.map(scientificAnalysis)
        let url = `/scientificAnalysis/search/${scientificAnalysis.searchedEntityType?.toLowerCase()}`
        let urlParams = {
            laboratoryIdUser: store.getters.labId,
            laboratoryIds: scientificAnalysis.laboratoryIds,
            scrollId: scrollId ?? undefined,
            size: params.size,
            totalElements: totalElements ?? undefined
        }
        const response = await axios.put(`${url}`, bodyParams, { params: urlParams })
        return response.data
    }

    public async summaryReport(
        groupingType: CaseSummaryReportGroupingTypeEnum,
        scientificAnalysis: ScientificAnalysis,
        selectedIds: number[]
    ): Promise<Blob> {
        const params = {
            laboratoryIdUser: store.getters.labId,
            laboratoryIds: scientificAnalysis.laboratoryIds,
            groupingType
        }

        const body = {
            filterGroups: this.map(scientificAnalysis),
            entityIdentifiersRequest: {
                entityIdentifiers: selectedIds
            }
        }

        const response = await axios.put(
            `/scientificAnalysis/search/case/summaryReport`,
            body,
            { params, responseType: 'arraybuffer' })
        return response.data
    }

    public async exportTo(
        exportType: CommonExportType,
        scientificAnalysis: ScientificAnalysis,
        sectionLabel: string,
        filterModel: CommonFilterModel,
        sortModel: Nullable<string[]>,
        columns: GridColumnRequest[],
        entityIdentifiers:number[]
    ): Promise<AxiosResponse<Blob>> {
        const params = {
            laboratoryIdUser: store.getters.labId,
            laboratoryIds: scientificAnalysis.laboratoryIds,
            page: 0,
            sort: sortModel,
            exportType
        }

        const body = {
            filterGroups: this.map(scientificAnalysis),
            gridColumns: {
                sectionLabel,
                filterModel,
                columns,
                entityIdentifiers
            }
        }

        return axios.put<Blob>(
            `/scientificAnalysis/search/${scientificAnalysis.searchedEntityType?.toLowerCase()}/export`,
            body,
            { params, responseType: 'arraybuffer' })
    }

    private map(scientificAnalysis: ScientificAnalysis): FilterGroupRequest[] {
        const request: FilterGroupRequest[] = []

        scientificAnalysis.entityFilters.forEach(iEntityFilter => {
            const iRequest: FilterGroupRequest = {
                entityType: iEntityFilter.entityType,
                groupedByParentEntity: iEntityFilter.groupedByParentEntity,
                filters: []
            }
            request.push(iRequest)

            iEntityFilter.filters.forEach(iFilter => {
                const iRequestCondition: FilterRequest = {
                    conditions: []
                }
                iRequest.filters.push(iRequestCondition)

                iFilter.fieldGroups.forEach(iFieldGroup => {
                    iFieldGroup.conditions.forEach(iCondition => {
                        iRequestCondition.conditions.push({
                            fieldName: iFieldGroup.field,
                            conditionType: iCondition.comparisonType.comparatorType,
                            value: (!iCondition.value ? null : iCondition.value),
                            valueTo: (iCondition.comparisonType.comparatorType === ScientificAnalysisComparatorType.IN_RANGE ? iCondition.valueTo : undefined)
                        })
                    })
                })
            })
        })

        return request
    }

    public async getAllTemplates(): Promise<ScientificAnalysisTemplateHeader[]> {
        const response = await axios.get<ScientificAnalysisTemplateHeader[]>(`/scientificAnalysis/templates`)
        return response.data
    }

    public async getTemplate(templateId: number): Promise<ScientificAnalysisSaveResponse> {
        const response = await axios.get < ScientificAnalysisSaveResponse>(`/scientificAnalysis/templates/${templateId}`)
        return response.data
    }

    public async importTemplate(file: File): Promise<ScientificAnalysisTemplateHeader[]> {
        const formData = new FormData()
        formData.append('file', file, file.name)
        let url = `/scientificAnalysis/templates/batchImport`
        const response = await axios.post <ScientificAnalysisTemplateHeader[]>(
            url,
            formData,
            {
                headers: { 'content-type': 'multipart/form-data' }
            }
        )
        return response.data
    }

    public async removeTemplate(templateId: number): Promise<boolean> {
        await axios.delete(`/scientificAnalysis/templates/${templateId}`)
        return true
    }

    public async renameTemplate(templateId: number, templateName: Nullable<string>): Promise<ScientificAnalysisTemplateHeader> {
        const request = {
            name: templateName
        }
        const response = await axios.put<ScientificAnalysisTemplateHeader>(`/scientificAnalysis/templates/${templateId}`, request)
        return response.data
    }

    public async updateTemplate(templateId: number, templateName: Nullable<string>, version: Nullable<number>, template: Nullable<string>): Promise<ScientificAnalysisTemplateHeader> {
        const request = {
            name: templateName,
            template,
            version
        }
        const response = await axios.put<ScientificAnalysisTemplateHeader>(`/scientificAnalysis/templates/${templateId}`, request)
        return response.data
    }

    public async createTemplate(templateName: string, version: number, template: string): Promise<ScientificAnalysisTemplateHeader> {
        const request = {
            name: templateName,
            template,
            version
        }
        const response = await axios.post<ScientificAnalysisTemplateHeader>(`/scientificAnalysis/templates`, request)
        return response.data
    }

    public async exportTemplates(templateIds: number[]): Promise<boolean> {
        let url = `/scientificAnalysis/templates/batchExport?`
        templateIds.forEach(templateId => {
            url += `templateId=${templateId}&`
        })

        axios.get(url,
            {
                responseType: 'arraybuffer'
            })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'template.anapath')
                document.body.appendChild(link)
                link.click()
            })
        return true
    }
}

export const scientificAnalysisService = new ScientificAnalysisServiceImpl()

export const loadedFilterEntityTypes: {
    [key in ScientificAnalysisFilterEntityType]?: ScientificAnalysisMetadataEntityFilterConditionsResponse;
} = {}
